import { useMutation } from '@tanstack/react-query'
import Button from 'ext-ui/components/Button/Button'
import React, {
  FormEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { AvatarSizes } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { TRANSACTION_COMMENT_REQUEST_TIMEOUT_MS } from 'src/queries/transactionConversations/transactionConversations.network'
import { transactionConversationMutations } from 'src/queries/transactionConversations/transactionConversations.queries'

import * as style from './TransactionCommentComposer.style'

interface TransactionCommentComposerProps {
  transactionId: string
  onPosted: (messageId: string) => void
}

const TransactionCommentComposer = ({
  transactionId,
  onPosted,
}: TransactionCommentComposerProps): ReactElement => {
  const translate = useTranslate()
  const currentUser = useCurrentUser()
  const { mutateAsync: postMessage } = useMutation(
    transactionConversationMutations.postMessage(),
  )
  const triggerRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const restoreTriggerFocusRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [isSending, setIsSending] = useState(false)
  const [posted, setPosted] = useState(false)

  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus()
      return
    }

    if (restoreTriggerFocusRef.current) {
      restoreTriggerFocusRef.current = false
      triggerRef.current?.focus()
    }
  }, [isOpen])

  const discardDraft = (restoreTriggerFocus: boolean): void => {
    if (isSending) return

    restoreTriggerFocusRef.current = restoreTriggerFocus
    setDraft('')
    setError(undefined)
    setIdempotencyKey(undefined)
    setPosted(false)
    setIsOpen(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Escape' || isSending) return

    event.preventDefault()
    discardDraft(true)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSending || !draft.trim()) return

    const nextIdempotencyKey = idempotencyKey ?? crypto.randomUUID()
    if (!idempotencyKey) {
      setIdempotencyKey(nextIdempotencyKey)
    }

    setError(undefined)
    setPosted(false)
    setIsSending(true)

    const requestController = new AbortController()
    const timeoutId = window.setTimeout(
      () => requestController.abort(),
      TRANSACTION_COMMENT_REQUEST_TIMEOUT_MS,
    )
    const rejectOnAbort = new Promise<never>((_, reject) => {
      requestController.signal.addEventListener(
        'abort',
        () => reject(new Error('Transaction comment request timed out')),
        { once: true },
      )
    })

    try {
      const response = await Promise.race([
        postMessage({
          transactionId,
          body: draft,
          idempotencyKey: nextIdempotencyKey,
          signal: requestController.signal,
        }),
        rejectOnAbort,
      ])
      setDraft('')
      setIdempotencyKey(undefined)
      setPosted(true)
      onPosted(response.id)
      restoreTriggerFocusRef.current = true
      setIsOpen(false)
    } catch {
      setError(translate('Something went wrong. Please try again.'))
    } finally {
      window.clearTimeout(timeoutId)
      setIsSending(false)
    }
  }

  const errorId = 'transaction-comment-error'
  const commentAuthor = currentUser ?? {
    id: 'current-user',
    fullNameOrEmail: translate('You'),
    initials: translate('You').charAt(0),
  }

  return (
    <div css={style.root}>
      {isOpen ? (
        <form css={style.composer} onSubmit={handleSubmit}>
          <span css={style.avatar} data-testid="transaction-comment-avatar">
            <UserAvatar
              decorative
              size={AvatarSizes.Small24}
              user={commentAuthor}
            />
          </span>
          <div css={style.content}>
            <div css={style.field} data-testid="transaction-comment-field">
              <label css={style.visuallyHidden} htmlFor="transaction-comment">
                {translate('Comment')}
              </label>
              <textarea
                ref={textareaRef}
                css={style.textarea}
                id="transaction-comment"
                placeholder={translate('Add a comment...')}
                value={draft}
                disabled={isSending}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={Boolean(error)}
                onChange={(event) => {
                  const nextDraft = event.target.value
                  if (error && nextDraft !== draft) {
                    setIdempotencyKey(undefined)
                  }
                  setDraft(nextDraft)
                  setError(undefined)
                  setPosted(false)
                }}
                onKeyDown={handleKeyDown}
              />
              <div css={style.actions}>
                <Button
                  type="button"
                  variant="secondary"
                  dropShadow={false}
                  disabled={isSending}
                  onClick={() => discardDraft(true)}
                >
                  {translate('Cancel')}
                </Button>
                <Button
                  type="submit"
                  dropShadow={false}
                  loading={isSending}
                  disabled={!draft.trim()}
                >
                  {translate(isSending ? 'Sending…' : 'Comment')}
                </Button>
              </div>
            </div>
            {error && (
              <p id={errorId} css={style.error} role="alert">
                {error}
              </p>
            )}
          </div>
        </form>
      ) : (
        <div css={style.restComposer}>
          <span css={style.restAvatar} data-testid="transaction-comment-avatar">
            <UserAvatar
              decorative
              size={AvatarSizes.Small24}
              user={commentAuthor}
            />
          </span>
          <button
            ref={triggerRef}
            type="button"
            css={style.restTrigger}
            data-testid="transaction-comment-rest-trigger"
            aria-label={translate('Comment')}
            onClick={() => {
              setPosted(false)
              setIsOpen(true)
            }}
          >
            <span css={style.placeholder}>{translate('Add a comment...')}</span>
            <span css={style.inlineAction}>{translate('Comment')}</span>
          </button>
        </div>
      )}
      {posted && (
        <p css={style.status} role="status" aria-atomic="true">
          {translate('Comment posted.')}
        </p>
      )}
    </div>
  )
}

export default TransactionCommentComposer
